import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { twMerge } from 'tailwind-merge';

import { RESULTS_CONTROL, RESULTS_CONTROL_IDLE } from './consts';

export type SortMode = 'game' | 'release';
export type MemberTab = 'plays' | 'futures';

/**
 * The controls row above the play sections (#2004): "Hide props & futures" on the left,
 * then the member's Plays/Futures switch and the sort on the right.
 *
 * Plays/Futures is the segmented control because it's a view switch between two peers —
 * the same shape as the tab strip it replaces, now sitting in line with the other
 * controls. Sort is a dropdown rather than a second segmented control: it's a property
 * of the list rather than a view, and two segmented controls side by side read as one
 * confused group.
 */
export function ResultsControls({
  hideProps,
  onHideProps,
  sort,
  onSort,
  memberTab,
  onMemberTab,
  showMemberTabs = false,
}: {
  hideProps: boolean;
  onHideProps: (next: boolean) => void;
  sort: SortMode;
  onSort: (next: SortMode) => void;
  memberTab?: MemberTab;
  onMemberTab?: (next: MemberTab) => void;
  showMemberTabs?: boolean;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {showMemberTabs && memberTab && onMemberTab && (
          <div className="inline-flex h-8 shrink-0 items-center rounded-lg bg-zinc-100 p-0.5 dark:bg-white/5">
            {(
              [
                ['plays', 'Plays'],
                ['futures', 'Futures'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => onMemberTab(value)}
                aria-pressed={memberTab === value}
                className={twMerge(
                  'inline-flex h-full items-center rounded-md px-3 font-semibold text-xs transition',
                  memberTab === value
                    ? 'bg-white text-zinc-900 shadow-sm dark:bg-white/15 dark:text-white'
                    : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-white',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <label className="flex items-center gap-2">
          <span className="font-medium text-[11px] text-zinc-400 dark:text-zinc-500">Sort</span>
          <span className="relative inline-flex">
            <select
              value={sort}
              onChange={(e) => onSort(e.target.value as SortMode)}
              className={twMerge(
                RESULTS_CONTROL,
                RESULTS_CONTROL_IDLE,
                'appearance-none pr-8 text-zinc-900 dark:text-white',
              )}
            >
              <option value="game">Game time</option>
              <option value="release">Release time</option>
            </select>
            <ChevronDownIcon className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-2 size-4 text-zinc-400" />
          </span>
        </label>
      </div>

      <button
        type="button"
        onClick={() => onHideProps(!hideProps)}
        aria-pressed={hideProps}
        className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-300"
      >
        <span
          className={twMerge(
            'relative h-5 w-9 shrink-0 rounded-full transition',
            hideProps ? 'bg-emerald-600' : 'bg-zinc-300 dark:bg-white/15',
          )}
        >
          <span
            className={twMerge(
              'absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow-sm transition',
              hideProps && 'translate-x-4',
            )}
          />
        </span>
        Hide props &amp; futures
      </button>
    </div>
  );
}
